import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import LegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { oidcSignInRedirect } from 'agentHub/auth/oidc/signIn'
import { reportClientError } from 'services/errorReportingService'

interface Props {
  dismissable?: boolean
}

// Shown in place of the v1 email-entry form when `?login-v2=true` (see
// useIsLoginV2Enabled). One button starts the OIDC authorize redirect.
function AgentHubLoginV2Form({ dismissable }: Props) {
  const intl = useIntl()
  const [isRedirecting, setIsRedirecting] = useState(false)

  const handleSignIn = useCallback(() => {
    setIsRedirecting(true)
    oidcSignInRedirect().catch((error) => {
      setIsRedirecting(false)
      reportClientError(error, { context: 'oidcSignInRedirect' }).catch(() => undefined)
      showSnackbar(
        intl.formatMessage({
          id: 'MCKts9GjL1ZL5f1_-z0e',
          defaultMessage: 'Could not start sign-in. Please try again.',
          description: 'Error snackbar shown when the agent hub OIDC sign-in redirect fails to start',
        }),
        'critical',
      )
    })
  }, [intl])

  return <>
    <ModalHeader title="" dismissible={dismissable} />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={24} data-testid="agent-hub-login-v2-form">
          <Group direction="horizontal" horizontalAlign="center"><LogoIcon /></Group>

          <Group direction="vertical" gap={4}>
            <Heading variant="heading4" align="center">
              {intl.formatMessage({
                id: 'fqXEN6y4cfbCyzLCFwi0',
                defaultMessage: 'Welcome back!',
                description: 'Title on the agent hub Auth V2 (OIDC) login modal shown above the sign-in button',
              })}
            </Heading>
            <BodyText variant="medium" align="center">
              {intl.formatMessage({
                id: 'vpwWvhmMcLEoYRb6qSSa',
                defaultMessage: 'Sign in to access your {displayShortName} portal.',
                description: 'Subtitle on the agent hub Auth V2 (OIDC) login modal shown beneath the welcome title',
              }, {
                displayShortName: agentHubOrSubBrandConfig.displayShortName,
              })}
            </BodyText>
          </Group>

          <TextButton
            kind="primary"
            size="large"
            data-testid="agent-hub-login-v2-button"
            fit="flex"
            disabled={isRedirecting}
            onClick={handleSignIn}
          >
            {intl.formatMessage({
              id: '1hLDTxBcMHmdzmyfXnK3',
              defaultMessage: 'Sign in with email and password',
              description: 'Agent Hub OIDC login button that starts the sign-in redirect',
            })}
          </TextButton>

          <LegalText />
        </Group>
      </ModalContent>
    </ModalBody>
  </>
}

export default AgentHubLoginV2Form
